import React, { useEffect, useRef } from "react";
import { attachAnalyser, getAudioContext } from "@/lib/audioGraph";

/**
 * OrbWaveform — a gold frequency line that pulses through the centre of the orb
 * when Jarvis is speaking. Hooks into the audio element via Web Audio API.
 *
 * Pass the current speaking HTMLAudioElement via `audioEl`. The component lazily
 * creates an AudioContext + AnalyserNode on first attach.
 */
export default function OrbWaveform({ audioEl, color = "#ffd34d", active = true }) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Hook AnalyserNode to the audio element via the shared audioGraph
  // helper (single source of truth — kills double-create errors that
  // silenced agent TTS).
  useEffect(() => {
    if (!audioEl) return;
    try {
      const ctx = getAudioContext();
      if (!ctx) return;
      ctxRef.current = ctx;
      const analyser = attachAnalyser(audioEl, { fftSize: 1024, smoothing: 0.78 });
      if (!analyser) return;
      analyserRef.current = analyser;

      const resumeIfNeeded = () => { if (ctx.state === "suspended") ctx.resume().catch(() => {}); };
      audioEl.addEventListener("play", resumeIfNeeded);
      return () => audioEl.removeEventListener("play", resumeIfNeeded);
    } catch (e) {
      console.warn("[OrbWaveform] init error:", e?.message);
    }
  }, [audioEl]);

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx2d = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      const w = canvas.width;
      const h = canvas.height;
      ctx2d.clearRect(0, 0, w, h);

      const analyser = analyserRef.current;
      if (!analyser || !active) return;

      const bufferLen = analyser.fftSize;
      const data = new Uint8Array(bufferLen);
      analyser.getByteTimeDomainData(data);

      // Centreline waveform across the canvas
      const cy = h / 2;
      const amp = h * 0.35;

      // Glow pass (thicker, blurred)
      ctx2d.lineWidth = 8 * dpr;
      ctx2d.shadowBlur = 28 * dpr;
      ctx2d.shadowColor = color;
      ctx2d.strokeStyle = color;
      ctx2d.globalAlpha = 0.55;
      ctx2d.beginPath();
      for (let i = 0; i < bufferLen; i++) {
        const v = (data[i] - 128) / 128;          // -1..1
        const x = (i / (bufferLen - 1)) * w;
        const y = cy + v * amp;
        if (i === 0) ctx2d.moveTo(x, y);
        else ctx2d.lineTo(x, y);
      }
      ctx2d.stroke();

      // Hot core pass
      ctx2d.lineWidth = 2 * dpr;
      ctx2d.shadowBlur = 12 * dpr;
      ctx2d.globalAlpha = 1;
      ctx2d.strokeStyle = "#fff7d6";
      ctx2d.beginPath();
      for (let i = 0; i < bufferLen; i++) {
        const v = (data[i] - 128) / 128;
        const x = (i / (bufferLen - 1)) * w;
        const y = cy + v * amp;
        if (i === 0) ctx2d.moveTo(x, y);
        else ctx2d.lineTo(x, y);
      }
      ctx2d.stroke();
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [color, active]);

  return (
    <canvas
      ref={canvasRef}
      className="rc-orb-wave"
      data-testid="orb-waveform"
      style={{
        position: "absolute",
        left: 0, right: 0, top: "50%",
        width: "100%",
        height: "30%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        zIndex: 5,
      }}
    />
  );
}
